import { useState, useCallback } from 'react';
import { message } from 'antd';
import {
  inspectionReportService,
  InspectionReport,
  InspectionReportLog,
  pickEditableReport,
} from '@/services/inspectionReport.service';
import { inspectionReportReviewService } from '@/services/inspectionReportReview.service';
import { InspectionReportReview } from '@/shared/types/inspectionReportReview.types';
import { InspectionItemStatus } from '@/shared/enums/InspectionStatus';
import { LaudoWizardData } from '../types/laudo-wizard.types';

interface UseLaudoReportsProps {
  itemId: string | undefined;
  currentItemStatus: InspectionItemStatus | undefined;
  isProvider: boolean;
}

interface UseLaudoReportsReturn {
  reports: InspectionReport[];
  loading: boolean;
  pendingReview: InspectionReportReview | null;
  reviewHistory: InspectionReportReview[];
  reportLogs: InspectionReportLog[];
  selectedReport: InspectionReport | null;

  setSelectedReport: (report: InspectionReport | null) => void;

  loadReports: () => Promise<void>;
  parseReportWizardData: (report: InspectionReport) => LaudoWizardData | null;
}
export function useLaudoReports({
  itemId,
  currentItemStatus,
  isProvider,
}: UseLaudoReportsProps): UseLaudoReportsReturn {
  const [reports, setReports] = useState<InspectionReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState<InspectionReport | null>(null);
  const [pendingReview, setPendingReview] = useState<InspectionReportReview | null>(null);
  const [reviewHistory, setReviewHistory] = useState<InspectionReportReview[]>([]);
  const [reportLogs] = useState<InspectionReportLog[]>([]);

  const loadReports = useCallback(async () => {
    if (!itemId) return;

    setLoading(true);
    try {
      const reportsData = await inspectionReportService.getReportsByInspectionItemId(itemId);
      setReports(reportsData);

      if (reportsData.length > 0) {
        const latestReport = pickEditableReport(reportsData);

        if (!latestReport) {
          setPendingReview(null);
          setReviewHistory([]);
          return;
        }

        try {
          const allReviews =
            await inspectionReportReviewService.getReviewsByInspectionItemId(itemId);
          setReviewHistory(allReviews);

          const latestReview = allReviews.length > 0 ? allReviews[0] : null;

          const statusIndicatesRejection =
            currentItemStatus === InspectionItemStatus.LaudoNegadoPorPrestadora ||
            currentItemStatus === InspectionItemStatus.LaudoNegadoPorBanco ||
            currentItemStatus === InspectionItemStatus.CorrecaoPeloInspetorBanco ||
            currentItemStatus === InspectionItemStatus.DevolvidoPelaPrestadora;

          if (latestReview && latestReview.status === 'Rejected' && statusIndicatesRejection) {
            setPendingReview(latestReview);

            if (
              latestReview.reviewerType === 'Bank' &&
              currentItemStatus === InspectionItemStatus.LaudoNegadoPorBanco &&
              isProvider
            ) {
              setSelectedReport(latestReport);
            }
          } else {
            setPendingReview(null);
          }
        } catch {
          setPendingReview(null);
          setReviewHistory([]);
        }
      } else {
        setPendingReview(null);
        setReviewHistory([]);
      }
    } catch {
      message.error('Erro ao carregar laudos');
    } finally {
      setLoading(false);
    }
  }, [itemId, currentItemStatus, isProvider]);

  const parseReportWizardData = useCallback((report: InspectionReport): LaudoWizardData | null => {
    if (!report.reportData) {
      return null;
    }

    try {
      const reportDataParsed = JSON.parse(report.reportData);
      return reportDataParsed.wizardData || null;
    } catch {
      return null;
    }
  }, []);

  return {
    reports,
    loading,
    pendingReview,
    reviewHistory,
    reportLogs,
    selectedReport,
    setSelectedReport,
    loadReports,
    parseReportWizardData,
  };
}
